import React from 'react'
import { Badge, Tab, Tabs } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { ALARM_SECTIONS, ALARM_SECTION_COLORS, ALARM_SECTION_TEXT_COLOR } from './alarmSections'

/**
 * The two sections of the alarms screen, as tabs above the table, each with its own pending count.
 *
 * Tabs rather than another row of toggle buttons: the screen already has a Notifications / Alarms toggle
 * that filters by LEVEL, and the sections split by ORIGIN. Two rows of look-alike buttons would read as
 * one control.
 *
 * @param {{value: string, onChange: function(string): void, counts: {general: number, automation: number}}} props
 *   The selected section, how to change it, and the pending alarms of each section
 */
const AlarmSectionTabs = ({ value, onChange, counts = {} }) => {
  const { t_ } = useSmartTranslation()
  // A count of 0 draws no badge, so a quiet section stays quiet. Each section keeps the colour it has on
  // the alarm button, so the tab and the button's half read as the same thing.
  const labelWithCount = (label, count, section) => (
    <Badge
      badgeContent={count ?? 0}
      data-testid={'alarm-section-count-' + section}
      sx={{ pr: 1.5, '.MuiBadge-badge': { bgcolor: ALARM_SECTION_COLORS[section], color: ALARM_SECTION_TEXT_COLOR, fontWeight: 700, fontVariantNumeric: 'tabular-nums' } }}
    >
      {label}
    </Badge>
  )
  return (
    <Tabs value={value} onChange={(_, next) => onChange(next)} aria-label={t_('Alarms')}>
      <Tab
        value={ALARM_SECTIONS.GENERAL}
        label={labelWithCount(t_('General alarms'), counts.general, ALARM_SECTIONS.GENERAL)}
        data-testid='alarm-section-tab-GENERAL'
      />
      <Tab
        value={ALARM_SECTIONS.AUTOMATION}
        label={labelWithCount(t_('Automation alarms'), counts.automation, ALARM_SECTIONS.AUTOMATION)}
        data-testid='alarm-section-tab-AUTOMATION'
      />
    </Tabs>
  )
}

export default AlarmSectionTabs
